import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

import map from "../../assets/map.jpg";
import vertexShader from "!raw-loader!../../assets/shaders/vertex.glsl";
import fragmentShader from "!raw-loader!../../assets/shaders/fragment.glsl";

import atmosphereVertexShader from "!raw-loader!../../assets/shaders/atmosphereVertex.glsl";
import atmosphereFragmentShader from "!raw-loader!../../assets/shaders/atmosphereFragment.glsl";

import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { PlanetItems } from "../type";

interface PlanetProps {
  addEolienneTrigger: number;
  addUsineTrigger: number;
  addCarTrigger: number;
  addHydroTrigger: number;
  addFarmTrigger: number;
  addAtmosphereColorTrigger: number;
}

export const Planet = (props: PlanetProps) => {
  const mountRef = useRef<HTMLDivElement>(null);

  const sphereRadius = 5;

  let colorMalusValue = 0.1;

  useEffect(() => {
    if (!mountRef.current) return;

    const correctedWidth = window.innerWidth / 2;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      correctedWidth / window.innerHeight,
      0.1,
      1000,
    );
    scene.add(camera);

    // Set renderer settings.
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(correctedWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Append renderer to dom.
    if (mountRef.current) {
      mountRef.current.appendChild(renderer.domElement);
    }

    colorMalusValue = 0.2 + (-props.addAtmosphereColorTrigger * 1.25) / 100;

    // Create planet sphere mesh.
    const planet = new THREE.Mesh(
      new THREE.SphereGeometry(sphereRadius, 50, 50),
      new THREE.ShaderMaterial({
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        uniforms: {
          globeTexture: {
            value: new THREE.TextureLoader().load(map),
          },
          colorMalus: {
            value: colorMalusValue,
          },
        },
      }),
    );

    // Add planet to scene.
    scene.add(planet);

    // Create atmoshpere mesh.
    const atmoshpere = new THREE.Mesh(
      new THREE.SphereGeometry(sphereRadius, 50, 50),
      new THREE.ShaderMaterial({
        vertexShader: atmosphereVertexShader,
        fragmentShader: atmosphereFragmentShader,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
        uniforms: {
          colorMalus: {
            value: colorMalusValue,
          },
        },
      }),
    );

    // Make the atmosphere 1.1 bigger than the planet.
    atmoshpere.scale.set(1.1, 1.1, 1.1);

    if (atmoshpere.material instanceof THREE.ShaderMaterial) {
      atmoshpere.material.uniforms.colorMalus.value = colorMalusValue;
    }

    // Add atmoshpere to scene.
    scene.add(atmoshpere);

    const starGeometry = new THREE.BufferGeometry();
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
    });

    const starVertices = [];
    const starsAmount = 10000;
    const starDistance = 3500;
    for (let i = 0; i < starsAmount; i++) {
      const x = (Math.random() - 0.5) * starDistance;
      const y = (Math.random() - 0.5) * starDistance;
      const z = (Math.random() - 0.5) * starDistance;
      starVertices.push(x, y, z);
    }

    starGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(starVertices, 3),
    );
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5); // Soft white ambient light
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1); // White directional light
    directionalLight.position.set(0, 1, 0); // Set light position
    scene.add(directionalLight);

    // Create group of factorys.
    const factoryGroup = new THREE.Group();
    planet.add(factoryGroup);

    const addObjectAtRandPos = (type: PlanetItems) => {
      let objectPath = process.env.PUBLIC_URL + "/assets/models/factory.obj";
      let material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
      });

      let objectScale = 0.1;

      if (type == "eolienne") {
        objectPath =
          process.env.PUBLIC_URL + "/assets/models/WindGenerator.obj";

        // Create a material with the desired color (white)
        material = new THREE.MeshBasicMaterial({
          color: 0xdddddd,
        });
      } else if (type == "car") {
        objectPath = process.env.PUBLIC_URL + "/assets/models/car.obj";

        // Create a material with the desired color (white)
        material = new THREE.MeshBasicMaterial({
          color: 0xadd8e6,
        });
      } else if (type == "farm") {
        objectPath = process.env.PUBLIC_URL + "/assets/models/farmland.obj";

        // Create a material with the desired color (white)
        material = new THREE.MeshBasicMaterial({
          color: 0xd2b48c,
        });
      } else if (type == "hydro") {
        objectScale = 0.01;
        objectPath =
          process.env.PUBLIC_URL + "/assets/models/hydroGenerator.obj";

        // Create a material with the desired color (white)
        material = new THREE.MeshBasicMaterial({
          color: 0xffff00,
        });
      }

      // instantiate a loader
      const loader = new OBJLoader();

      loader.load(objectPath, function (object) {
        // Generate random positions within the sphere's bounds
        const randLat = Math.random() * Math.PI - Math.PI / 2; // Random latitude in radians (-π/2 to π/2)
        const randLong = Math.random() * 2 * Math.PI; // Random longitude in radians (0 to 2π)

        const pointOnSphere = new THREE.Vector3(
          sphereRadius * Math.cos(randLat) * Math.cos(randLong),
          sphereRadius * Math.sin(randLat),
          sphereRadius * Math.cos(randLat) * Math.sin(randLong),
        );

        object.position.copy(pointOnSphere);

        object.scale.set(objectScale, objectScale, objectScale);

        // Get normal of the point on the sphere
        const normal = pointOnSphere.clone().normalize();

        // Align the factory with the normal
        object.lookAt(normal);

        // Random rotation around the z-axis
        object.rotateZ(Math.random() * Math.PI * 2 * 180);

        // Traverse the loaded object's children and apply the material
        object.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.material = material;
          }
        });

        factoryGroup.add(object);
      });
    };

    if (props.addEolienneTrigger > 0) {
      for (let i = 0; i < props.addEolienneTrigger * 5; i++) {
        addObjectAtRandPos("eolienne");
      }
    }

    if (props.addUsineTrigger > 0) {
      for (let i = 0; i < props.addUsineTrigger * 5; i++) {
        addObjectAtRandPos("usine");
      }
    }

    if (props.addCarTrigger > 0) {
      for (let i = 0; i < props.addCarTrigger * 3; i++) {
        addObjectAtRandPos("car");
      }
    }

    if (props.addHydroTrigger > 0) {
      for (let i = 0; i < props.addHydroTrigger * 3; i++) {
        addObjectAtRandPos("hydro");
      }
    }

    if (props.addFarmTrigger > 0) {
      for (let i = 0; i < props.addFarmTrigger * 3; i++) {
        addObjectAtRandPos("farm");
      }
    }

    camera.position.z = 15;

    // Set controls settings.
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.enablePan = false;
    controls.minDistance = 10;
    controls.maxDistance = 15;
    controls.rotateSpeed = 0.8;
    controls.zoomSpeed = 1;
    controls.autoRotate = false;
    controls.minPolarAngle = Math.PI / 3.5;
    controls.maxPolarAngle = Math.PI - Math.PI / 3;

    const planetRotation = () => {
      planet.rotation.y += 0.005;
    };

    // Animation loop.
    const animate = () => {
      requestAnimationFrame(animate);

      planetRotation();

      controls.update();

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [
    props.addEolienneTrigger,
    props.addUsineTrigger,
    props.addCarTrigger,
    props.addFarmTrigger,
    props.addHydroTrigger,
    props.addAtmosphereColorTrigger,
  ]);

  return <div ref={mountRef} style={{ width: "50%", height: "100vh" }} />;
};
